/**
 * Feature checker helper for React app
 * Checks if a feature is enabled for an account
 * @param {Object} account - The account object from the API
 * @param {string} featureName - The name of the feature to check
 * @returns {boolean} - True if the feature is enabled, false otherwise
 */
export const isFeatureEnabled = (account, featureName) => {
  if (!account || !featureName) {
    return false;
  }

  if (!account.features || !Array.isArray(account.features)) {
    return false;
  }

  return account.features.includes(featureName);
};

/**
 * Get all enabled features for an account
 * @param {Object} account - The account object from the API
 * @returns {string[]} - Array of enabled feature names, or empty array if none
 */
export const getEnabledFeatures = (account) => {
  if (!account || !account.features || !Array.isArray(account.features)) {
    return [];
  }

  return account.features;
};

/**
 * Build a minimal account-shaped object from a conversation survey API response.
 * Used as a fallback when there is no authenticated `auth.currentAccount()` —
 * for example, unauthenticated users opening a post-conversation survey link
 * from email still need their account's feature flags evaluated so they get
 * the v2 design instead of falling back to legacy.
 *
 * @param {Object|null|undefined} conversationSurvey - Survey from /api/v1/conversation_surveys/:uuid
 * @returns {{ features: string[] }|null}
 */
export const accountFromConversationSurvey = (conversationSurvey) => {
  if (!conversationSurvey || !Array.isArray(conversationSurvey.account_features)) {
    return null;
  }

  return { features: conversationSurvey.account_features };
};

/**
 * Phone calls are available when Phone Calls or Phone Calls Only is enabled.
 * Mirrors Account#phone_calls_enabled? on the backend.
 * @param {Object} account - The account object from the API
 * @returns {boolean}
 */
export const isPhoneCallsEnabled = (account) =>
  isFeatureEnabled(account, FEATURES.PHONE_CALLS) || isFeatureEnabled(account, FEATURES.PHONE_CALLS_ONLY);

/**
 * Incentivized phone calls only apply when calls are available and the
 * incentive feature is enabled — mirrors Account#incentivized_calls_enabled?
 * on the backend.
 * @param {Object} account - The account object from the API
 * @returns {boolean}
 */
export const isIncentivizedCallsEnabled = (account) =>
  isPhoneCallsEnabled(account) && isFeatureEnabled(account, FEATURES.INCENTIVIZED_PHONE_CALLS);

// Feature name constants for type safety and consistency
export const FEATURES = {
  KEYWORD_MANAGEMENT: 'Keyword Management System',
  AI_ENHANCED_KMS: 'AI Enhanced KMS',
  PII_DETECTION: 'PII Detection',
  PHONE_CALLS: 'Phone Calls',
  INCENTIVIZED_PHONE_CALLS: 'Incentivized Phone Calls',
  DISALLOW_ATTACHMENTS_IN_MESSAGES: 'Disallow Attachments In Messages',
  CONVERSATION_COMPLIANCE_ACKNOWLEDGMENT: 'Conversation Compliance Acknowledgment',
  TRANSLATIONS: 'Conversation translations',
  MESSAGE_TRIGGER_NOTES: 'Message Trigger Notes',
  NEW_UI: 'New UI',
  PATIENT_DEMOGRAPHICS: 'Patient Demographics',
  USE_QUESTIONNAIRE_BUILDER: 'Use Questionnaire Builder',
  PHONE_CALLS_ONLY: 'Phone Calls Only',
};
